import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { HitomeqErrorType } from "@/commons/models/SystemError";
import { PlanType, Plan, PlanDetail } from "@/commons/services/Plans";
import { WaiterServicePlanType } from "@/commons/services/WaiterServicePlans";
import { FamilyPermittedFunctions } from "@/commons/services/Families";

/**
 * 施設 API 操作クラス
 */
export default class Facilities {
  /** 共通サービス */
  private commonService: CommonService;

  public static readonly serviceNameMapByType: Map<string, string> = new Map<string, string>([
    ["0", "本部"],
    ["11", "訪問介護"],
    ["61", "予防訪問介護"],
    ["12", "訪問入浴介護"],
    ["62", "予防訪問入浴介護"],
    ["14", "訪問リハ"],
    ["64", "予防訪問リハ"],
    ["15", "通所介護"],
    ["65", "予防通所介護"],
    ["16", "通所リハビリテーション"],
    ["66", "予防通所リハビリテーション"],
    ["21", "短期入所生活介護"],
    ["24", "予防短期入所生活介護"],
    ["22", "短期入所療養介護"],
    ["25", "予防短期入所療養介護"],
    ["27", "特定施設入居者生活介護（短期利用）"],
    ["28", "地域密着型特定施設入居者生活介護（短期利用）"],
    ["32", "認知症対応型共同生活介護"],
    ["37", "予防認知症対応型共同生活介護"],
    ["33", "特定施設入居者生活介護"],
    ["35", "予防特定施設入居者生活介護"],
    ["36", "地域密着型特定施設入居者生活介護"],
    ["38", "認知症対応型共同生活介護（短期利用）"],
    ["39", "予防認知症対応型共同生活介護（短期利用）"],
    ["51", "介護老人福祉施設"],
    ["52", "介護老人保健施設"],
    ["54", "地域密着型介護福祉施設"],
    ["71", "夜間対応型訪問介護"],
    ["72", "認知症対応型通所介護"],
    ["74", "予防認知症対応型通所介護"],
    ["78", "地域密着型通所介護"],
    ["7s", "小規模多機能"],
    ["73", "小規模多機能居宅介護"],
    ["68", "小規模多機能居宅介護（短期利用）"],
    ["7_", "予防小規模多機能"],
    ["75", "予防小規模多機能居宅介護"],
    ["69", "予防小規模多機能居宅介護（短期利用）"],
    ["7k", "看護小規模多機能"],
    ["77", "看護小規模多機能居宅介護"],
    ["79", "看護小規模多機能居宅介護（短期利用）"],
    ["99", "サービス付き高齢者住宅"],
    ["A_", "総合事業"],
    ["A1", "訪問型サービス（みなし）"],
    ["A2", "訪問型サービス（独自）"],
    ["A3", "訪問型サービス（独自／定率）"],
    ["A4", "訪問型サービス（独自／定額）"],
    ["A5", "通所型サービス（みなし）"],
    ["A6", "通所型サービス（独自）"],
    ["A7", "通所型サービス（独自／定率）"],
    ["A8", "通所型サービス（独自／定額）"],
    ["A9", "その他生活支援サービス（配食／定率）"],
    ["AA", "その他生活支援サービス（配食／定額）"],
    ["AB", "その他生活支援サービス（見守り／定率）"],
    ["AC", "その他生活支援サービス（見守り／定額）"],
    ["AD", "その他生活支援サービス（その他／定率）"],
    ["AE", "その他生活支援サービス（その他／定額）"]
  ]);

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * GET Facilities
   */
  public async get(userStore: LoginUser, query?: { targetFacilityId: number | string }): Promise<FacilityGetModel> {
    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Facility, formatParams);
    const response = await this.commonService.executeGet<FacilityGetModel>(userStore, requestUrl, this.constructor.name, query, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * PATCH Facilities
   */
  public async patch(userStore: LoginUser, model: FacilityPatchModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Facility, formatParams);
    const response = await this.commonService.executePatch<any>(userStore, requestUrl, model, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
}

/**
 * 施設
 */
export interface FacilityGetModel {
  /** 施設の営業時間開始 */
  businessHourStart?: number;
  /** 施設の営業時間終了 */
  businessHourEnd?: number;
  /** 施設を運営する会社を特定する ID */
  companyId?: number;
  /** 住所 */
  companyAddress?: string;
  /** 会社名 */
  companyName?: string;
  /** 読み方 */
  companyKana?: string;
  /** 電話番号 */
  companyTel?: string;
  /** 論理削除フラグ */
  deleteFlag: boolean;
  /** 施設を示すID */
  id: number;
  /** 施設の住所 */
  address: string;
  /** 施設の名前 */
  name: string;
  /** 施設のカナ */
  kana?: string;
  /** 代表者名 */
  representative?: string;
  /** 施設の電話番号 */
  tel: string;
  /** 施設 HP */
  url: string;
  /** 施設の写真 */
  imageUrl: string;
  /** 登録日 */
  created: number;
  /** 更新日 */
  updated: number;
  /** 家族連絡サービス開始日 */
  validPeriodFrom: number | null;
  /** 家族連絡サービス終了日 */
  validPeriodTo: number | null;
  /** 待機者管理サービス開始日 */
  waiterServiceValidPeriodFrom: number | null;
  /** 待機者管理サービス終了日 */
  waiterServiceValidPeriodTo: number | null;
  /** 家族連絡サービスの使用可否 */
  useFamilyService: boolean;
  /** 家族連絡サービス契約プラン種別 */
  planType: PlanType;
  /** 家族連絡サービス契約プラン */
  plan: Plan;
  /**
   * エンタープライズのみ格納される、エンタープライズ契約情報
   * プランマスターのレコードと同じ形式の情報が格納されている
   */
  planDetail?: PlanDetail;
  /** 入所者募集・待機者管理サービスの使用可否 */
  useWaiterService: boolean;
  /** 入所者募集・待機者管理サービス契約プラン種別 */
  waiterServicePlanType: WaiterServicePlanType;
  /** 使用許可機能 */
  permittedFunctions: PermittedFunctions;
  /** 家族・施設関係者のLINEbot操作の通知設定 */
  notifications: Notifications;
  /** メッセージ送信設定 */
  messageSettings: MessageSettings;
  /** デフォルトの家族の機能使用可否 */
  defaultFamilyPermittedFunctions: FamilyPermittedFunctions;
  /** 利用者移動可能施設。 */
  residentTransferableFacilities: number[];
  /** 施設関係者グループメンバー移動可能施設。 */
  otherUserTransferableFacilities: number[];
  /** 閲覧可能施設。系列施設のID */
  waiterViewableFacilities: number[];
  /** 使用する入居申込みテンプレートID */
  useAdmissionApplicationTemplateId: number | null;
  /** 使用する入居評価テンプレートID */
  useAdmissionJudgementTemplateId: number | null;
  /** 待機者管理の部屋設定 */
  recruitmentRoom: RecruitmentRoom;
  /** 待機者管理の部屋設定(施設固有) */
  customRecruitmentRoom: Array<CustomRecruitmentRoom>;
  /** 受信メッセージ案内文(家族) */
  receiveMessageGuidanceToFamilies: string;
  /** 受信メッセージ案内文(施設関係者) */
  receiveMessageGuidanceToOtherUserGroups: string;
  /** 受信メッセージ案内文(待機者) */
  receiveMessageGuidanceToWaiters: string;

  /** 他事業所サービスの使用可否 */
  useOtherOfficeConnectionService: boolean;

  /** 連携システム設定 */
  cooperationSystems: CooperationSystems;
  /** 事業所番号とサービス種別のMAP配列 */
  careOfficeNumbers: Array<CareOfficeNumbers>;
  /** HitomeQCareサポートの施設ID */
  hitomeQCareSupportFacilityIds: Array<number>;

  /** 施設情報のオプション情報 */
  facilityDetailInfo?: FacilityDetailInfo | null;
  /** 施設情報の受け入れ情報 */
  facilityDetailAccept?: FacilityDetailAccept | null;
}

/**
 * ### 使用許可機能
 *
 * - 各メイン機能の使用許可を持つ
 */
export interface PermittedFunctions {
  // 家族連絡サービス
  /** 介護記録 */
  careimage: boolean;
  /** 家族からの連絡 */
  opinion: boolean;
  /** 面会予約 */
  reservation: boolean;
  /** オンライン面会 */
  onlineReservation: boolean;
  /** 外出外泊届け */
  outing: boolean;
  /** 写真印刷依頼 */
  picture: boolean;
  /** イベント情報 */
  event: boolean;
  /** 施設関係者からの連絡 */
  otherUserOpinion: boolean;

  // 待機者管理サービス
  /** 家族からの連絡 */
  waiterOpinion: boolean;
}

export interface Notifications {
  // 家族ユーザー
  /** イベント参加申込み/内容変更/キャンセル */
  event: Array<NotificationObject>;
  /** 家族登録/登録削除 */
  family: Array<NotificationObject>;
  /** 家族からの連絡、メッセージ送信の家族からの返信 */
  message: Array<NotificationObject>;
  /** 家族メッセージで本文を表示するか */
  messageShowText: boolean;
  /** 写真印刷依頼 */
  picture: Array<NotificationObject>;
  /** 面会予約/キャンセル（訪問面会、オンライン面会は同一設定） */
  reservation: Array<NotificationObject>;
  /** 外出外泊届け/キャンセル */
  outing: Array<NotificationObject>;

  // 施設関係者グループ
  /** 施設関係者登録/登録削除 */
  otherUser: Array<NotificationObject>;
  /** 施設関係者からの連絡、メッセージ送信の施設関係者からの返信 */
  otherUserMessage: Array<NotificationObject>;
  /** 施設関係者メッセージで本文を表示するか */
  otherUserMessageShowText: boolean;

  // 他事業所連携
  /** トピック送信・返信 */
  otherOfficeMessage: Array<NotificationObject>;
  /** 他事業所連携メッセージで本文を表示するか */
  otherOfficeMessageShowText: boolean;
  /** オンライン会議 */
  otherOfficeOnlineMeeting: Array<NotificationObject>;
}

export interface NotificationObject {
  /** 通知対象の家族グループor施設関係者グループID */
  groupId: number;
  /** 通知先メールアドレス */
  mailAddress: string[];
}

export interface MessageSettings {
  /** メッセージ返信期限。日数を数値で持つ */
  replyLimit: number;
}

export interface RecruitmentRoom {
  [key: string]: boolean;

  /** 従来型多床室 */
  multiBed: boolean;
  /** 従来型個室 */
  conventinalSingle: boolean;
  /** ユニット型個室 */
  unitTypeSingle: boolean;
  /** ２人部屋（夫婦部屋） */
  double: boolean;
  /** 個室 */
  single: boolean;
}

export interface CustomRecruitmentRoom {
  /** ID */
  id: string;
  /** 名称 */
  name: string;
  /** 略名 */
  abbreviationName: string;
  /** 色(RGB値) */
  color: string;
  /** 使用可否 */
  use: boolean;
}

export interface FacilityPatchModel {
  /** 施設の名前 */
  name: string;
  /** 施設の電話番号 */
  tel: string;
  /** 施設 HP */
  url: string;
  /** 施設の写真 */
  imageUrl?: string;
  /** 使用許可機能 */
  permittedFunctions?: PermittedFunctions;
  /** デフォルトの家族の機能使用可否 */
  defaultFamilyPermittedFunctions?: FamilyPermittedFunctions;
  /** 使用する入居申込みテンプレートID */
  useAdmissionApplicationTemplateId?: number | null;
  /** 使用する入居評価テンプレートID */
  useAdmissionJudgementTemplateId?: number | null;
  /** 待機者管理の部屋設定 */
  recruitmentRoom?: RecruitmentRoom | null;
  /** 待機者管理の部屋設定 */
  customRecruitmentRoom?: Array<CustomRecruitmentRoom>;

  /** 受信メッセージ案内文(家族) */
  receiveMessageGuidanceToFamilies?: string;
  /** 受信メッセージ案内文(施設関係者) */
  receiveMessageGuidanceToOtherUserGroups?: string;
  /** 受信メッセージ案内文(待機者) */
  receiveMessageGuidanceToWaiters?: string;

  /** 施設情報のオプション情報 */
  facilityDetailInfo?: FacilityDetailInfo | null;
  /** 施設情報の受け入れ情報 */
  facilityDetailAccept?: FacilityDetailAccept | null;

  /** 更新日 */
  updated: number;
}

export interface CooperationSystems {
  /** ケアツリー */
  careTree: boolean;
  /** hitomeqツリー */
  hitomeQTree: boolean;
  /** HitomeQ ケアサポート */
  hitomeQCareSupport: boolean;
  /** iMedic */
  iMedic: boolean;
}

export interface CareOfficeNumbers {
  /** 事業所番号 */
  careOfficeNumber: number;
  /** サービス種別 */
  careOfficeServiceType: string;
  /** サービス名 */
  careOfficeName: string;
}
/**
 * ## 施設
 */
export interface FacilityDetail {
  /** 施設ID */
  facilityId?: number | null;
  /** 種別 */
  itemType?: FacilityItemType | null;

  /** 登録日 */
  created: number;
  /** 更新日 */
  updated: number;
}

export enum FacilityItemType {
  "INFO" = "INFO#",
  "ACCEPT" = "ACCEPT#"
}

/**
 * ### 施設情報のオプション情報
 */
export interface FacilityDetailInfo extends FacilityDetail {
  /** 住所情報 */
  addressInfo?: Address | null;
  /** 地域情報 */
  region?: Region | null;
  /** 施設種別 */
  facilityCategory?: FacilityCategory | null;
}

export interface Address {
  /** 郵便番号 */
  postalCode: string;
  /** 都道府県 */
  prefecture: string;
  /** 市区町村 */
  municipality: string;
  /** 丁目番地号 */
  streetAddress: string;
}

export interface Region {
  /** 都道府県コード */
  prefectureCode: string;
  /** 市区町村コード */
  municipalityCode: string;
}

export enum FacilityCategory {
  /** 介護付有料老人ホーム */
  KAIGO_YUURYOU = "kaigoYuuryou",
  /** 住宅型有料老人ホーム */
  JYUUTAKU_YUURYOU = "jyuutakuYuuryou",
  /** サービス付き高齢者向け住宅 */
  SERVICE_JYUUTAKU = "serviceJyuutaku",
  /** シニア向け分譲マンション */
  CONDOMINIUMS = "condominiums",
  /** グループホーム */
  GROUP_HOME = "groupHome",
  /** ケアハウス */
  CARE_HOUSE = "careHouse",
  /** 特別養護老人ホーム（特養） */
  TOKUYO = "tokuyo",
  /** 介護老人保健施設（老健） */
  KAIGO_HOKEN = "kaigoHoken",
  /** 介護医療院 */
  MEDICAL_CENTER = "medicalCenter",
  /** その他 */
  OTHER = "other"
}

/**
 * ### 施設情報の受け入れ情報
 */
export interface FacilityDetailAccept extends FacilityDetail {
  /** 介護度（複数選択可能） */
  careLevels?: CareLevels | undefined;
  /** 生活保護を受け入れるか否か */
  acceptsWelfare: boolean;
  /** 医療・看護体制 */
  medicalNursings: MedicalNursings;
}

export interface CareLevels {
  /** 自立 */
  careLevel1000: boolean;
  /** 申請中 */
  careLevel1010: boolean;
  /** 要支援１ */
  careLevel2010: boolean;
  /** 要支援２ */
  careLevel2020: boolean;
  /** 要介護1 */
  careLevel3010: boolean;
  /** 要介護2 */
  careLevel3020: boolean;
  /** 要介護3 */
  careLevel3030: boolean;
  /** 要介護4 */
  careLevel3040: boolean;
  /** 要介護5 */
  careLevel3050: boolean;
}

export interface MedicalNursings {
  /** 介護食 */
  nursingCareFood: boolean;
  /** 流動食・嚥下食 */
  liquidDysphagiaDiet: boolean;
  /** 糖尿病・インスリン */
  diabetesInsulin: boolean;
  /** 胃ろう */
  gastrostomy: boolean;
  /** 鼻腔・経管栄養 */
  nasogastricTube: boolean;
  /** ストーマ・人工肛門 */
  stomaOrColostomy: boolean;
  /** 在宅酸素療法 */
  homeOxygenTherapy: boolean;
  /** 人工呼吸器 */
  ventilator: boolean;
  /** 褥瘡・床ずれ */
  bedsore: boolean;
  /** たん吸引 */
  phlegmSuction: boolean;
  /** 人工透析 */
  dialysis: boolean;
  /** 気管切開 */
  tracheostomy: boolean;
  /** 中心静脈栄養 */
  totalParenteralNutrition: boolean;
  /** カテーテル・尿バルーン */
  urinaryCatheterOrBalloonCatheter: boolean;
  /** ペースメーカー */
  pacemaker: boolean;
}
